import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Trophy,
  Clock,
  Rocket,
  Zap,
  Snail,
  BarChart3,
  CalendarDays,
} from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";

const STAGE_LABELS = {
  new_lead: "New Lead",
  contacted: "Contacted",
  consultation_booked: "Consult Booked",
  proposal_sent: "Proposal Sent",
  won: "Won",
  lost: "Lost",
};

const STAGE_COLORS = {
  new_lead: "bg-blue-500",
  contacted: "bg-indigo-500",
  consultation_booked: "bg-violet-500",
  proposal_sent: "bg-amber-500",
  won: "bg-emerald-500",
  lost: "bg-slate-400",
};

function fmtDays(n) {
  if (n === null || n === undefined) return "—";
  if (n < 1) {
    const hrs = Math.max(1, Math.round(n * 24));
    return `${hrs} hr${hrs === 1 ? "" : "s"}`;
  }
  return `${n} day${n === 1 ? "" : "s"}`;
}

export default function AnalyticsView({ onOpenClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get("/leads/analytics");
        if (!cancelled) setData(res.data);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center text-slate-500">Loading analytics...</CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center text-slate-500">No analytics available yet.</CardContent>
      </Card>
    );
  }

  const tm = data.thisMonth;
  const lm = data.lastMonth;
  const delta = (a, b) => (a - b);
  const leadsDelta = delta(tm.leads, lm.leads);
  const wonDelta = delta(tm.won, lm.won);
  const rateDelta = +(tm.conversionRate - lm.conversionRate).toFixed(1);

  const maxAvg = Math.max(
    1,
    ...data.avgDaysPerStage.map((s) => s.avgDays || 0)
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="analytics-view"
    >
      {/* Headline metrics */}
      <div className="grid gap-3 md:grid-cols-4">
        <Metric
          icon={<CalendarDays size={18} />}
          label="This Month — New Leads"
          value={tm.leads}
          delta={leadsDelta}
          deltaLabel="vs last month"
          testid="metric-month-leads"
        />
        <Metric
          icon={<Trophy size={18} />}
          label="This Month — Won"
          value={tm.won}
          delta={wonDelta}
          deltaLabel="vs last month"
          testid="metric-month-won"
        />
        <Metric
          icon={<TrendingUp size={18} />}
          label="This Month — Win Rate"
          value={`${tm.conversionRate}%`}
          delta={rateDelta}
          deltaUnit="pts"
          deltaLabel="vs last month"
          testid="metric-month-rate"
        />
        <Metric
          icon={<Clock size={18} />}
          label="Avg Lead → Won"
          value={fmtDays(data.avgDaysToWin)}
          subtle={`across ${data.totalWonAllTime} won client${data.totalWonAllTime === 1 ? "" : "s"}`}
          testid="metric-avg-to-win"
        />
      </div>

      {/* Avg days per stage */}
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-5">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
            <BarChart3 size={18} /> Average Time Spent Per Stage
          </h3>
          <div className="space-y-3" data-testid="avg-stage-chart">
            {data.avgDaysPerStage.map((row) => {
              const pct = row.avgDays ? Math.max(8, (row.avgDays / maxAvg) * 100) : 0;
              return (
                <div key={row.stage} className="grid grid-cols-12 items-center gap-3">
                  <div className="col-span-12 flex items-center justify-between text-sm sm:col-span-3">
                    <span className="font-medium text-slate-900">{STAGE_LABELS[row.stage] || row.stage}</span>
                    <span className="text-xs text-slate-500 sm:hidden">
                      {fmtDays(row.avgDays)}
                    </span>
                  </div>
                  <div className="col-span-12 sm:col-span-7">
                    <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${STAGE_COLORS[row.stage] || "bg-slate-500"}`}
                        style={{ width: row.avgDays ? `${pct}%` : "0%" }}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 hidden text-right text-sm sm:col-span-2 sm:block">
                    <span className="font-semibold text-slate-900">{fmtDays(row.avgDays)}</span>
                    {row.samples > 0 && (
                      <p className="text-xs text-slate-500">n={row.samples}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {data.avgDaysPerStage.every((s) => !s.avgDays) && (
              <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                Move some leads through stages to populate stage timing.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Fastest + Slowest paths */}
      <div className="grid gap-4 md:grid-cols-2">
        <PathCard
          label="Fastest Conversion"
          icon={<Zap size={18} className="text-emerald-700" />}
          tone="bg-emerald-50 border-emerald-200"
          path={data.fastest}
          onOpenClient={onOpenClient}
          testid="fastest-path"
        />
        <PathCard
          label="Slowest Conversion"
          icon={<Snail size={18} className="text-amber-700" />}
          tone="bg-amber-50 border-amber-200"
          path={data.slowest}
          onOpenClient={onOpenClient}
          testid="slowest-path"
        />
      </div>

      {/* Velocity + Win-rate tables */}
      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold">
              <Rocket size={18} /> Conversion Velocity by Source
            </h3>
            {data.velocityBySource.length === 0 ? (
              <p className="text-sm text-slate-500">No won clients yet.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="velocity-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3 text-right">Won</th>
                      <th className="py-2 text-right">Avg Lead → Won</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.velocityBySource.map((row) => (
                      <tr key={row.source} className="border-b last:border-0">
                        <td className="py-2 pr-3 font-medium">{row.source}</td>
                        <td className="py-2 pr-3 text-right text-slate-700">{row.wonCount}</td>
                        <td className="py-2 text-right font-semibold">{fmtDays(row.avgDaysToWin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold">
              <Trophy size={18} /> Win Rate by Source
            </h3>
            {data.winRateBySource.length === 0 ? (
              <p className="text-sm text-slate-500">No source data yet.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="win-rate-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3 text-right">Total</th>
                      <th className="py-2 pr-3 text-right">Won</th>
                      <th className="py-2 text-right">Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.winRateBySource.map((row) => (
                      <tr key={row.source} className="border-b last:border-0">
                        <td className="py-2 pr-3 font-medium">{row.source}</td>
                        <td className="py-2 pr-3 text-right text-slate-700">{row.total}</td>
                        <td className="py-2 pr-3 text-right text-emerald-700">{row.won}</td>
                        <td className="py-2 text-right">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              row.winRate >= 30
                                ? "bg-emerald-100 text-emerald-800"
                                : row.winRate >= 10
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {row.winRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

function Metric({ icon, label, value, delta, deltaLabel, deltaUnit, subtle, testid }) {
  const trend =
    delta === undefined || delta === null
      ? null
      : delta > 0
        ? { Icon: TrendingUp, cls: "text-emerald-700" }
        : delta < 0
          ? { Icon: TrendingDown, cls: "text-red-600" }
          : { Icon: Minus, cls: "text-slate-500" };
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <p className="text-sm text-slate-500">{label}</p>
          <span className="text-slate-400">{icon}</span>
        </div>
        <p data-testid={testid} className="mt-2 text-3xl font-bold">
          {value}
        </p>
        {trend && (
          <p className={`mt-1 flex items-center gap-1 text-xs font-medium ${trend.cls}`}>
            <trend.Icon size={12} />
            {delta > 0 ? "+" : ""}
            {delta}
            {deltaUnit ? ` ${deltaUnit}` : ""} {deltaLabel}
          </p>
        )}
        {subtle && <p className="mt-1 text-xs text-slate-500">{subtle}</p>}
      </CardContent>
    </Card>
  );
}

function PathCard({ label, icon, tone, path, onOpenClient, testid }) {
  return (
    <Card className={`rounded-2xl border-2 shadow-sm ${tone}`}>
      <CardContent className="p-5" data-testid={testid}>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
          {icon} {label}
        </div>
        {!path ? (
          <p className="text-sm text-slate-600">No won clients yet.</p>
        ) : (
          <button
            onClick={() => onOpenClient?.(path.clientId)}
            className="w-full text-left transition hover:opacity-80"
          >
            <p className="text-xl font-bold text-slate-900">{path.dogName || "—"}</p>
            <p className="text-sm text-slate-700">{path.ownerName}</p>
            <div className="mt-3 flex items-end justify-between">
              <p className="text-3xl font-extrabold tabular-nums">{fmtDays(path.days)}</p>
              <p className="text-xs text-slate-600">
                via <span className="font-medium">{path.source}</span>
              </p>
            </div>
          </button>
        )}
      </CardContent>
    </Card>
  );
}
